import { useCallback } from 'react';
import {
  DataTableDataSearchDto,
  TableColumnConfigDto,
  TableDataDto,
} from '@asset-metrix/portal-gateway-client-js';
import { ICellRendererParams } from 'ag-grid-community';
import { uniq } from 'lodash';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '../../store';

type PageFilterColumnConfig = TableColumnConfigDto & {
  type: 'pageFilter';
  filterKey: string;
};

export function isPageFilterColumnConfig(
  c: TableColumnConfigDto
): c is PageFilterColumnConfig {
  return c.type === 'pageFilter' && c.filterKey !== null;
}

type SetFilterFromPageFilterColumnProps = {
  pageFilterColumn?: TableColumnConfigDto;
  pageFilterInstanceId: string;
  pageFilterSelections: DataTableDataSearchDto['selection'];
};

export function usePageFilterColumnState({
  pageFilterColumn,
  pageFilterInstanceId,
  pageFilterSelections,
}: SetFilterFromPageFilterColumnProps) {
  const { visualisedFilter: pageFilter } = useStore(
    (state) => state.filterData[pageFilterInstanceId]
  ) || { visualisedFilter: undefined };

  const [setFilterSelection, resetFilterSelection] = useStore(
    useShallow((state) => [
      state.setFilterSelection,
      state.resetFilterSelection,
    ])
  );

  return {
    onChange: useCallback(
      (
        event: React.ChangeEvent<HTMLInputElement>,
        params: ICellRendererParams<TableDataDto['items'][number]>
      ) => {
        const filterKey = pageFilterColumn?.filterKey;

        if (filterKey == null) {
          return;
        }

        let selections = pageFilterSelections?.[filterKey] ?? undefined;

        const currentPageFilter = pageFilter?.[filterKey];
        if (currentPageFilter != null) {
          if (Array.isArray(selections)) {
            selections = uniq(
              event.target.checked && params.value != null
                ? [...selections, params.value]
                : selections.filter((value) => value !== params.value)
            );

            if (selections.length === 0) {
              resetFilterSelection(pageFilterInstanceId, filterKey);
              return;
            }
          }
          if (typeof selections === 'string') {
            selections = event.target.checked ? params.value : undefined;
            if (selections == null) {
              resetFilterSelection(pageFilterInstanceId, filterKey);
              return;
            }
          }

          setFilterSelection(
            pageFilterInstanceId,
            filterKey,
            currentPageFilter.type,
            currentPageFilter.display,
            selections
          );
        }
      },
      [
        pageFilterColumn,
        pageFilter,
        pageFilterInstanceId,
        setFilterSelection,
        resetFilterSelection,
        pageFilterSelections,
      ]
    ),
    isChecked: useCallback(
      (params: ICellRendererParams<TableDataDto['items'][number]>) => {
        if (pageFilterColumn?.filterKey == null) {
          return false;
        }

        const selections = pageFilterSelections?.[pageFilterColumn?.filterKey];

        if (Array.isArray(selections)) {
          return (selections as unknown[]).includes(params.value);
        }
        if (typeof selections === 'string') {
          return selections === params.value;
        }
        return false;
      },
      [pageFilterColumn, pageFilterSelections]
    ),
  };
}
